import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

// Scroll reveal wrapper
export function Reveal({ children, delay = 0, y = 28, className, once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Masked line reveal for hero headline
export function MaskLine({ children, delay = 0, className }) {
  return (
    <span className={cn("block overflow-hidden", className)}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// Animated counter
export function Counter({ to, suffix = "", duration = 2, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
      else setVal(to);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
}

// Section overline label
export function Overline({ children, className }) {
  return (
    <div className={cn("flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan", className)}>
      <span className="h-px w-8 bg-brand-cyan/60" />
      {children}
    </div>
  );
}

export function SectionHeading({ overline, title, subtitle, align = "left", className }) {
  return (
    <div className={cn(align === "center" ? "text-center items-center" : "", "flex flex-col", className)}>
      {overline && (
        <Reveal>
          <Overline className={align === "center" ? "justify-center" : ""}>{overline}</Overline>
        </Reveal>
      )}
      <Reveal delay={0.05}>
        <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {title}
        </h2>
      </Reveal>
      {subtitle && (
        <Reveal delay={0.1}>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg">{subtitle}</p>
        </Reveal>
      )}
    </div>
  );
}

// Buttons
export function PrimaryButton({ children, className, ...props }) {
  return (
    <button
      className={cn(
        "group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-7 py-3.5 font-medium text-[#08120A] transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(198,255,58,0.45)]",
        className
      )}
      {...props}
    >
      <span className="relative z-10 flex items-center gap-2">{children}</span>
      <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0" />
    </button>
  );
}

export function GhostButton({ children, className, ...props }) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-medium text-white backdrop-blur-md transition-colors duration-300 hover:border-brand-cyan/50 hover:bg-white/10",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
